import React, { useEffect } from "react"

import Modal from "@kit/design/modal"
import Stack from "@kit/design/stack"
import { Note } from "@kit/design/note"
import { useMediaBreakpoint } from "@kit/design/use-media-breakpoint"

import { useCurrentUser } from "hooks/use-current-user"
import { analytics } from "utils/analytics"
import { ExternalAnalyticsLink } from "utils/external-analytics-link"
import { consiceInterval } from "../../../../pages/upgrades/utils"
import { PlanCard } from "./plan-card"

import { usePlanComparisonContext } from "./context"

import { Tabs, TabList, Tab, TabPanels, TabPanel } from "@reach/tabs"

const GRID_COLUMN_CLASSES = {
  1: "grid-cols-1",
  2: "grid-cols-2",
  3: "grid-cols-3",
  4: "grid-cols-4"
}

const LargeScreenLayout = ({ plans }) => {
  return (
    <div
      data-testid="plan-comparison-content-large"
      className={`grid ${GRID_COLUMN_CLASSES[plans.length]} gap-6`}
    >
      {plans.map(plan => (
        <PlanCard key={plan.id} plan={plan} multiplePlans={plans.length > 1} />
      ))}
    </div>
  )
}

const MobileLayout = ({ plans }) => {
  const testId = "plan-comparison-content-mobile"

  // Tabs are unnecessary when there is only one plan to show
  if (plans.length <= 1) {
    return (
      <div data-testid={testId}>
        {plans.map(plan => (
          <PlanCard key={plan.id} plan={plan} multiplePlans={false} />
        ))}
      </div>
    )
  }

  const padding = plans.length < 3 ? "px-4" : "px-1"
  const priceFontSize = plans.length < 3 ? "text-3xl" : "text-2xl"

  return (
    <div data-testid={testId}>
      <Tabs orientation="horizontal">
        <TabList
          className={`grid ${GRID_COLUMN_CLASSES[plans.length]} gap-0 border border-1 border-gray-200 rounded-lg text-left p-1 mb-4`}
        >
          {plans.map(plan => {
            const intervalAriaLabel = plan.interval
              ? `per ${plan.interval}`
              : null

            return (
              <Tab
                key={plan.id}
                className={`rounded-lg py-2 ${padding} text-left data-[selected]:bg-gray-50 data-[selected]:focus:outline-none`}
              >
                <Stack spacing={2}>
                  <h2 className="text-black font-semibold leading-8">
                    {plan.display_name}
                  </h2>
                  <h2 className="text-black font-heading">
                    <span className={priceFontSize}>{plan.text}</span>
                    <span className="text-xl" aria-label={intervalAriaLabel}>
                      /{consiceInterval(plan.interval)}
                    </span>
                  </h2>
                </Stack>
              </Tab>
            )
          })}
        </TabList>
        <TabPanels>
          {plans.map(plan => (
            <TabPanel key={plan.id}>
              <PlanCard plan={plan} multiplePlans={true} />
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </div>
  )
}

const COLUMN_WIDTHS = {
  1: "576px",
  2: "920px"
}

const PlanComparisonContent = ({ title, description }) => {
  const {
    visiblePlans,
    closeAllModals,
    promoPlan,
    interval,
    currentPlan,
    serviceLevels
  } = usePlanComparisonContext()
  const user = useCurrentUser()

  useEffect(() => {
    if (!interval || visiblePlans.length > 0) return

    analytics.track("Element", "Viewed", {
      element_type: "note",
      name: "plan_comparison_no_plan_available",
      account_id: user.account_id,
      current_plan_id: currentPlan?.id,
      current_plan_name: currentPlan?.name,
      service_levels: serviceLevels,
      interval
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interval, visiblePlans.length])

  const isLargeScreen = useMediaBreakpoint("lg")
  const maxContainerWidth = COLUMN_WIDTHS[visiblePlans.length] || ""

  return (
    <Modal
      onClose={closeAllModals}
      label={<h2 className="text-xl font-semibold leading-6">{title}</h2>}
      ariaLabel={title}
      width={isLargeScreen ? "fit-content" : "100%"}
      className="max-h-fit! m-auto p-2"
    >
      <Stack
        spacing={8}
        className="h-full md:m-auto"
        style={{ maxWidth: maxContainerWidth }}
      >
        <p className="text-gray-800 -mt-3 md:-mt-6">{description}</p>
        {promoPlan?.promo_code?.valid && (
          <Note variant="success">
            <Note.Icon />
            <Note.Content>
              <Note.Heading level={3}>Discount applied!</Note.Heading>
              <p>{promoPlan.promo_code.message}</p>
            </Note.Content>
          </Note>
        )}
        {!visiblePlans.length > 0 && (
          <div className="grid grid-cols-1">
            <Note layout="inline">
              <Note.Icon />
              <Note.Content>
                <p>
                  We can&apos;t show upgrade options for your plan here. To
                  upgrade, email us at{" "}
                  <a href="mailto:help@kit.com">help@kit.com</a>.
                </p>
              </Note.Content>
            </Note>
          </div>
        )}
        {isLargeScreen ? (
          <LargeScreenLayout plans={visiblePlans} />
        ) : (
          <MobileLayout plans={visiblePlans} />
        )}
        <div className="text-center font-semibold text-gray-800 leading-8">
          <ExternalAnalyticsLink
            eventName="viewed_full_plan_comparison"
            eventProperties={{
              account_id: user.account_id,
              visible_plans: visiblePlans.map(plan => plan.id)
            }}
            href="https://convertkit.com/pricing"
            target="_blank"
            rel="nofollow noreferrer"
          >
            See full plan comparison
          </ExternalAnalyticsLink>
        </div>
      </Stack>
    </Modal>
  )
}

export { PlanComparisonContent }
